/**
 * What survives a page reload.
 *
 * The session itself lives in IndexedDB (`src/db/auth-store.ts`), but reading
 * it is asynchronous — on a mobile pull-to-refresh the shell would have to
 * decide what to render before Dexie has answered, and that is exactly how an
 * already signed-in operator ends up staring at the sign-in screen again.
 *
 * So a small snapshot of the active session is mirrored into `localStorage`,
 * which is synchronous: who is signed in, on which device, when they logged in
 * and when the session was last unlocked. On boot the shell reads that
 * snapshot, confirms it against the stored session, and lets the operator
 * straight back in instead of demanding the PIN again.
 *
 * Nothing secret is written here: no PIN, no digest, no token that would let
 * another device impersonate the account. The snapshot is only usable together
 * with the IndexedDB session *and* a matching device fingerprint.
 *
 * Pure and storage-agnostic on purpose — every function takes the storage it
 * should work against, so the whole thing is exercised outside a browser.
 */
import { storageKey } from './config';
import type { AuthSession, UnlockMethod, UserRole } from '../types/user';

/** Where the session snapshot and the last open screen are kept. */
export const SESSION_CACHE_KEY = storageKey('auth.session');
export const VIEW_CACHE_KEY = storageKey('ui.active-view');

/**
 * How long an unlock keeps carrying the session across reloads.
 *
 * Measured from the last time the app was *used*, not from the unlock itself —
 * every boot and every return to the foreground stamps it again. In practice a
 * daily user never meets the PIN screen once they are in; an installation left
 * untouched for half a day asks for the code again on the next launch.
 */
export const UNLOCK_GRACE_MS = 12 * 60 * 60 * 1000;

/** The slice of the Web Storage API this module needs. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** The mirrored session — identity, device binding and timestamps. */
export interface CachedSession {
  /** Who is signed in. */
  userId: string;
  /** Always `current`; kept so a snapshot from an older build is recognisable. */
  sessionId: string;
  deviceId: string;
  deviceFingerprint: string;
  role: UserRole;
  /** Login timestamp — when the session was issued. */
  issuedAt: string;
  expiresAt: string;
  unlockMethod: UnlockMethod;
  /** Epoch ms of the last unlock or foreground moment. */
  lastActiveAt: number;
}

/**
 * The browser's own storage, or `null` where there is none (server-side
 * rendering, a Node test run, or a browser that refuses access in private
 * mode). Every call site treats `null` as "nothing cached" rather than failing.
 */
export function defaultStorage(): StorageLike | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    return null;
  }
}

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

/** Builds the snapshot that goes to disk for a freshly unlocked session. */
export function toCachedSession(session: AuthSession, now: number = Date.now()): CachedSession {
  return {
    userId: session.userId,
    sessionId: session.id,
    deviceId: session.deviceId,
    deviceFingerprint: session.deviceFingerprint,
    role: session.role,
    issuedAt: session.issuedAt,
    expiresAt: session.expiresAt,
    unlockMethod: session.unlockMethod,
    lastActiveAt: now,
  };
}

/** Narrows anything parsed off disk to a snapshot, or `null` if it is not one. */
export function parseCachedSession(raw: string | null): CachedSession | null {
  if (!isNonEmptyString(raw)) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof value !== 'object' || value === null) return null;
  const candidate = value as Record<string, unknown>;
  const required = ['userId', 'sessionId', 'deviceId', 'deviceFingerprint', 'issuedAt', 'expiresAt'];
  if (!required.every((field) => isNonEmptyString(candidate[field]))) return null;
  if (typeof candidate.lastActiveAt !== 'number' || !Number.isFinite(candidate.lastActiveAt)) {
    return null;
  }
  return {
    userId: candidate.userId as string,
    sessionId: candidate.sessionId as string,
    deviceId: candidate.deviceId as string,
    deviceFingerprint: candidate.deviceFingerprint as string,
    role: (candidate.role as UserRole) ?? 'soldier',
    issuedAt: candidate.issuedAt as string,
    expiresAt: candidate.expiresAt as string,
    unlockMethod: (candidate.unlockMethod as UnlockMethod) ?? 'pin',
    lastActiveAt: candidate.lastActiveAt,
  };
}

export function readSessionCache(storage: StorageLike | null = defaultStorage()): CachedSession | null {
  if (!storage) return null;
  try {
    return parseCachedSession(storage.getItem(SESSION_CACHE_KEY));
  } catch {
    return null;
  }
}

/** Mirrors a session to disk. Called on every unlock and every refresh. */
export function writeSessionCache(
  session: AuthSession,
  options: { now?: number; storage?: StorageLike | null } = {},
): CachedSession {
  const entry = toCachedSession(session, options.now ?? Date.now());
  const storage = options.storage === undefined ? defaultStorage() : options.storage;
  try {
    storage?.setItem(SESSION_CACHE_KEY, JSON.stringify(entry));
  } catch {
    /* a full or blocked storage must never break signing in */
  }
  return entry;
}

/**
 * Re-stamps the snapshot without touching the session — what the shell calls
 * when it comes back to the foreground, so the unlock window follows real use.
 */
export function touchSessionCache(
  options: { now?: number; storage?: StorageLike | null } = {},
): CachedSession | null {
  const storage = options.storage === undefined ? defaultStorage() : options.storage;
  const cached = readSessionCache(storage);
  if (!cached) return null;
  const entry: CachedSession = { ...cached, lastActiveAt: options.now ?? Date.now() };
  try {
    storage?.setItem(SESSION_CACHE_KEY, JSON.stringify(entry));
  } catch {
    /* ignore */
  }
  return entry;
}

/**
 * Expires the unlock window while keeping the session — the "נעילה" button.
 * The next boot finds a session it may resume but an unlock it may not, and
 * lands on the PIN screen exactly as the operator asked.
 */
export function lockSessionCache(storage: StorageLike | null = defaultStorage()): void {
  const cached = readSessionCache(storage);
  if (!cached) return;
  try {
    storage?.setItem(SESSION_CACHE_KEY, JSON.stringify({ ...cached, lastActiveAt: 0 }));
  } catch {
    /* ignore */
  }
}

export function clearSessionCache(storage: StorageLike | null = defaultStorage()): void {
  try {
    storage?.removeItem(SESSION_CACHE_KEY);
    storage?.removeItem(VIEW_CACHE_KEY);
    // Signing out takes the open dialog with it — the next operator on this
    // device starts on the board, not inside somebody else's task.
    storage?.removeItem(OPEN_SCREEN_KEY);
  } catch {
    /* ignore */
  }
}

/** The snapshot describes this very session on this very device. */
export function matchesSession(
  cached: CachedSession | null | undefined,
  session: AuthSession,
): boolean {
  if (!cached) return false;
  if (cached.userId !== session.userId) return false;
  if (cached.sessionId !== session.id) return false;
  if (cached.deviceFingerprint !== session.deviceFingerprint) return false;
  // A re-issued session gets a new `issuedAt`; an older snapshot is not it.
  return cached.issuedAt === session.issuedAt;
}

/** The unlock is still within its window (a clock moved backwards counts). */
export function isUnlockFresh(
  cached: CachedSession | null | undefined,
  now: number = Date.now(),
  graceMs: number = UNLOCK_GRACE_MS,
): boolean {
  if (!cached) return false;
  if (cached.lastActiveAt <= 0) return false;
  return now - cached.lastActiveAt < graceMs;
}

/**
 * The whole rule in one place: may a reload put the operator straight back
 * into the app, or must the PIN screen be shown?
 *
 * Everything has to line up — the snapshot belongs to this session, the
 * session belongs to this device, it has not expired, and the unlock window is
 * still open. Anything less falls back to the PIN.
 */
export function canResumeWithoutUnlock(
  cached: CachedSession | null | undefined,
  session: AuthSession | null | undefined,
  deviceFingerprint: string,
  now: number = Date.now(),
  graceMs: number = UNLOCK_GRACE_MS,
): boolean {
  if (!cached || !session) return false;
  if (session.deviceFingerprint !== deviceFingerprint) return false;
  if (!matchesSession(cached, session)) return false;
  if (now >= Date.parse(cached.expiresAt)) return false;
  return isUnlockFresh(cached, now, graceMs);
}

// ── The screen the operator was on ───────────────────────────────────

/**
 * Remembers the open tab so a refresh comes back to it. The caller passes the
 * ids it knows, so a stale value from an older build is dropped instead of
 * navigating the shell nowhere.
 */
export function readActiveView<T extends string>(
  allowed: readonly T[],
  storage: StorageLike | null = defaultStorage(),
): T | null {
  if (!storage) return null;
  let raw: string | null = null;
  try {
    raw = storage.getItem(VIEW_CACHE_KEY);
  } catch {
    return null;
  }
  if (!isNonEmptyString(raw)) return null;
  return allowed.includes(raw as T) ? (raw as T) : null;
}

export function writeActiveView(view: string, storage: StorageLike | null = defaultStorage()): void {
  try {
    storage?.setItem(VIEW_CACHE_KEY, view);
  } catch {
    /* ignore */
  }
}

export function clearActiveView(storage: StorageLike | null = defaultStorage()): void {
  try {
    storage?.removeItem(VIEW_CACHE_KEY);
  } catch {
    /* ignore */
  }
}

// ── The dialog the operator had open ─────────────────────────────────

/** Where the open dialog is remembered across a reload. */
export const OPEN_SCREEN_KEY = storageKey('ui.open-screen');

/**
 * The dialog a screen had open, and what it was open on.
 *
 * Restoring the tab alone still dropped an operator who was mid-task back onto
 * the board: the task they had opened, the convoy they were editing and the
 * form they were filling all closed on the refresh. This snapshot is the other
 * half of that — `kind` is the screen's own vocabulary (`create`, `edit`,
 * `detail`, `teams`, `bin`), and `id` the record it was open on, if any.
 */
export interface OpenScreen {
  kind: string;
  id: string;
}

/**
 * Reads the snapshot back, dropping a `kind` this build does not know — the
 * same defence {@link readActiveView} applies to a stale tab id.
 */
export function readOpenScreen(
  allowed: readonly string[],
  storage: StorageLike | null = defaultStorage(),
): OpenScreen | null {
  if (!storage) return null;
  let raw: string | null = null;
  try {
    raw = storage.getItem(OPEN_SCREEN_KEY);
  } catch {
    return null;
  }
  if (!isNonEmptyString(raw)) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof value !== 'object' || value === null) return null;
  const candidate = value as Record<string, unknown>;
  const kind = typeof candidate.kind === 'string' ? candidate.kind : '';
  if (!allowed.includes(kind)) return null;
  return { kind, id: typeof candidate.id === 'string' ? candidate.id : '' };
}

/** Mirrors the open dialog. `null` records that nothing is open. */
export function writeOpenScreen(
  screen: OpenScreen | null,
  storage: StorageLike | null = defaultStorage(),
): void {
  try {
    if (!screen) {
      storage?.removeItem(OPEN_SCREEN_KEY);
      return;
    }
    storage?.setItem(OPEN_SCREEN_KEY, JSON.stringify(screen));
  } catch {
    /* ignore */
  }
}

export function clearOpenScreen(storage: StorageLike | null = defaultStorage()): void {
  writeOpenScreen(null, storage);
}
